// Shared blog types, validation and the tiny Markdown-ish renderer used by the
// public blog and the admin editor. Deliberately zero-dependency: no markdown
// library ships to the client, which keeps the blog off the JS budget.
import { z } from "zod";

export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  body: string;
  cover_image: string | null;
  cover_alt: string | null;
  tag: string;
  author: string;
  published: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  /** Editorial state: draft → pending (writer submitted) → published / rejected. */
  status: BlogStatus;
  submitted_at: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  author_id: string | null;
}

export type BlogStatus = "draft" | "pending" | "published" | "rejected";

export const STATUS_LABEL: Record<BlogStatus, string> = {
  draft: "Draft",
  pending: "Awaiting approval",
  published: "Published",
  rejected: "Changes requested",
};

export const BLOG_COLUMNS =
  "id, slug, title, excerpt, body, cover_image, cover_alt, tag, author, published, published_at, created_at, updated_at, status, submitted_at, reviewed_at, review_note, author_id";

export const BLOG_TAGS = [
  "Palworld TCG",
  "Palworld News",
  "Card Prices",
  "Deck Building",
  "Collecting",
  "Guides",
] as const;

export function slugifyTitle(input: string): string {
  return input
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

export const upsertPostSchema = z.object({
  id: z.string().uuid().optional(),
  slug: z
    .string()
    .min(3)
    .max(80)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Slug must be lowercase words separated by hyphens."),
  title: z.string().min(5).max(90),
  excerpt: z.string().max(300).default(""),
  body: z.string().max(120_000).default(""),
  cover_image: z.string().url().max(500).nullable().default(null),
  cover_alt: z.string().max(200).nullable().default(null),
  tag: z.string().min(2).max(40).default("Palworld TCG"),
  author: z.string().min(2).max(60).default("Pal Stars"),
  published: z.boolean().default(false),
  /** Writers submit with "pending"; admins publish directly. */
  status: z.enum(["draft", "pending", "published", "rejected"]).default("draft"),
});
export type UpsertPostInput = z.infer<typeof upsertPostSchema>;

export const draftPromptSchema = z.object({
  topic: z.string().min(4).max(200),
  angle: z.string().max(400).optional(),
  tag: z.string().max(40).optional(),
});

/** Words per minute a typical reader manages on wiki-style prose. */
export function readingMinutes(body: string): number {
  const words = body.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
}

export function formatPostDate(iso: string | null): string {
  if (!iso) return "Draft";
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" });
}

/* ------------------------------------------------------------------ blocks */

export type Block =
  | { kind: "h2" | "h3"; text: string }
  | { kind: "p"; text: string }
  | { kind: "quote"; text: string }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; items: string[] }
  | { kind: "img"; src: string; alt: string }
  | { kind: "youtube"; id: string }
  | { kind: "hr" };

const YT = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,20})/;

/**
 * Parses the editor body into a small block list. Supported syntax:
 *   ## / ###  headings · - or 1. lists · > quote · --- rule
 *   ![alt](https://image)  ·  a bare YouTube URL on its own line
 * Inline: **bold**, *italic*, `code`, [text](url)
 */
export function parseBlocks(body: string): Block[] {
  const lines = body.replace(/\r\n?/g, "\n").split("\n");
  const blocks: Block[] = [];
  let para: string[] = [];
  let list: { kind: "ul" | "ol"; items: string[] } | null = null;

  const flushPara = () => {
    if (para.length) {
      blocks.push({ kind: "p", text: para.join(" ").trim() });
      para = [];
    }
  };
  const flushList = () => {
    if (list) {
      blocks.push(list.kind === "ul" ? { kind: "ul", items: list.items } : { kind: "ol", items: list.items });
      list = null;
    }
  };
  const flush = () => {
    flushPara();
    flushList();
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flush();
      continue;
    }

    const img = /^!\[([^\]]*)\]\((\S+?)\)$/.exec(line);
    if (img) {
      flush();
      blocks.push({ kind: "img", alt: img[1] ?? "", src: img[2] ?? "" });
      continue;
    }

    const ytExplicit = /^!yt\[([A-Za-z0-9_-]{6,20})\]$/.exec(line);
    if (ytExplicit) {
      flush();
      blocks.push({ kind: "youtube", id: ytExplicit[1]! });
      continue;
    }
    if (/^https?:\/\/\S+$/.test(line)) {
      const yt = YT.exec(line);
      if (yt) {
        flush();
        blocks.push({ kind: "youtube", id: yt[1]! });
        continue;
      }
    }

    if (/^---+$/.test(line)) {
      flush();
      blocks.push({ kind: "hr" });
      continue;
    }
    if (line.startsWith("### ")) {
      flush();
      blocks.push({ kind: "h3", text: line.slice(4).trim() });
      continue;
    }
    if (line.startsWith("## ")) {
      flush();
      blocks.push({ kind: "h2", text: line.slice(3).trim() });
      continue;
    }
    if (line.startsWith("# ")) {
      // A single H1 belongs to the page, never the body.
      flush();
      blocks.push({ kind: "h2", text: line.slice(2).trim() });
      continue;
    }
    if (line.startsWith("> ")) {
      flush();
      blocks.push({ kind: "quote", text: line.slice(2).trim() });
      continue;
    }
    const ul = /^[-*]\s+(.*)$/.exec(line);
    if (ul) {
      flushPara();
      if (!list || list.kind !== "ul") {
        flushList();
        list = { kind: "ul", items: [] };
      }
      list.items.push(ul[1]!.trim());
      continue;
    }
    const ol = /^\d+[.)]\s+(.*)$/.exec(line);
    if (ol) {
      flushPara();
      if (!list || list.kind !== "ol") {
        flushList();
        list = { kind: "ol", items: [] };
      }
      list.items.push(ol[1]!.trim());
      continue;
    }

    flushList();
    para.push(line);
  }
  flush();
  return blocks;
}

/* ------------------------------------------------------------- inline spans */

export type Span =
  | { kind: "text"; text: string }
  | { kind: "strong"; text: string }
  | { kind: "em"; text: string }
  | { kind: "code"; text: string }
  | { kind: "link"; text: string; href: string };

const INLINE = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g;

export function parseInline(text: string): Span[] {
  const out: Span[] = [];
  let last = 0;
  for (const m of text.matchAll(INLINE)) {
    const token = m[0];
    const at = m.index ?? 0;
    if (at > last) out.push({ kind: "text", text: text.slice(last, at) });
    if (token.startsWith("**")) out.push({ kind: "strong", text: token.slice(2, -2) });
    else if (token.startsWith("`")) out.push({ kind: "code", text: token.slice(1, -1) });
    else if (token.startsWith("[")) {
      const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(token);
      if (link) out.push({ kind: "link", text: link[1]!, href: link[2]! });
      else out.push({ kind: "text", text: token });
    } else out.push({ kind: "em", text: token.slice(1, -1) });
    last = at + token.length;
  }
  if (last < text.length) out.push({ kind: "text", text: text.slice(last) });
  return out;
}

/** Plain text of a body, for excerpts and meta descriptions. */
export function plainText(body: string): string {
  return body
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/[#>*`_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function autoExcerpt(post: { excerpt: string; body: string }): string {
  const e = post.excerpt.trim();
  if (e) return e;
  return plainText(post.body).slice(0, 155);
}
